import { toast } from "react-toastify";

import {
  RollbackAgenticLogicRuleRequestBodyType,
  RollbackAgenticLogicRuleRequestPathParamsType,
  RollbackAgenticLogicRuleResponseBodyType,
} from "@intelligems/ig-types";
import { getMutationErrorMessage } from "@src/queryClient";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import { agenticLogicKeys } from "../queryKeys";
import { ApiRequest } from "../types/api";

/**
 * Unlike the shipping-provider equivalent, the target version travels in the
 * request BODY — the path is only `/rules/{ruleId}/rollback`.
 */
const rollbackAgenticLogicRule = async (
  payload: ApiRequest<
    RollbackAgenticLogicRuleRequestBodyType,
    RollbackAgenticLogicRuleRequestPathParamsType
  >
) => {
  const resp = await api.post<
    RollbackAgenticLogicRuleResponseBodyType,
    RollbackAgenticLogicRuleRequestBodyType
  >(
    `/v2/agentic-logic/rules/${payload.pathParams.ruleId}/rollback`,
    payload.body
  );
  return resp.data;
};

export const useRollbackAgenticLogicRule = () => {
  const queryClient = useQueryClient();

  return useMutation<
    RollbackAgenticLogicRuleResponseBodyType,
    Error,
    ApiRequest<
      RollbackAgenticLogicRuleRequestBodyType,
      RollbackAgenticLogicRuleRequestPathParamsType
    >,
    unknown
  >({
    mutationFn: async (data) => rollbackAgenticLogicRule(data),
    onSuccess: async (_data, payload) => {
      const { ruleId } = payload.pathParams;
      await queryClient.invalidateQueries({
        queryKey: agenticLogicKeys.versions(ruleId),
      });
      await queryClient.invalidateQueries({
        queryKey: agenticLogicKeys.rule(ruleId),
      });
    },
    onError: (error) => {
      const errorDetail = getMutationErrorMessage(error);
      toast.error(
        `Failed to rollback version${errorDetail ? `. ${errorDetail}` : ""}`
      );
    },
  });
};
